import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { popupFactory } from '@/factories'

import FeaturedTable from '../FeaturedTable/FeaturedTable'
import API from '../../api/api'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { selectUserData, selectUserRole } from '../../reducers/userDataReducer'
import PaginationFooter from '../PaginationFooter/PaginationFooter'
import Dashboard from '../Dashboard'
import Icon from '../Icons'
import ConfirmPopup from '../Popups/ConfirmPopup'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'

import TicketFilters from './components/TicketFilters'
import ServiceFeesModal from './components/ServiceFeesModal'
import SettingsModal from './components/SettingsModal'
import DetailsModal from './components/DetailsModal'
import { columns } from './helpers'
import AnalyticsWidget from './components/AnalyticsWidget'

const ParkingDashboard = ({ buildingData, userRole }) => {
  const { t } = useTranslation(['parking-dashboard', 'common'])
  const [analyticsOpen, toggleAnalytics] = useState(false)
  const [modalData, toggleModal] = useState(false)
  const [parkingData, setParkingData] = useState({ count: 0, tickets: [] })
  const [valetSettings, setValetSettings] = useState({})
  const [settingsOpen, toggleSettings] = useState(false)
  const [checkoutModalData, toggleCheckoutModal] = useState(false)

  const [statusFilter, setStatusFilter] = useState('')
  const [dateRange, setDateRange] = useState([null, null])
  const [phoneOrLicensePlateFilter, setPhoneOrLicensePlateFilter] = useState('')

  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const { displaySnackbar } = useSnackbar()

  const fetchParkingTickets = async () => {
    if (buildingData) {
      const resp = await API.getParkingDataDashboard({
        buildingId: buildingData.id,
        ...(dateRange[0] &&
          !Number.isNaN(dateRange[0]) && {
            startDate: moment(dateRange[0]).tz(buildingData.timezone, true).valueOf(),
          }),
        ...(dateRange[1] &&
          !Number.isNaN(dateRange[1]) && {
            endDate: moment(dateRange[1]).tz(buildingData.timezone, true).valueOf(),
          }),
        search: phoneOrLicensePlateFilter,
        status: statusFilter,
        limit: showOnPage,
        skip: (pageNumber - 1) * showOnPage,
      })
      setParkingData(resp.data)
    }
  }

  const debounceFn = debounce(fetchParkingTickets, 300)

  useEffect(() => {
    fetchParkingTickets()
  }, [buildingData.id])

  useEffect(() => {
    debounceFn()
  }, [buildingData.id, dateRange[0], dateRange[1], statusFilter, showOnPage, pageNumber])

  // Handle search input changes
  useEffect(() => {
    setPageNumber(1)
    debounceFn()
  }, [phoneOrLicensePlateFilter])

  const closeCheckoutModal = () => {
    toggleCheckoutModal(false)
  }

  const checkoutVehicle = async () => {
    try {
      await API.checkoutVehicle(checkoutModalData.id)
      displaySnackbar(t('parking-dashboard:notifications.checkedOutSuccess'))
      debounceFn()
    } catch {
      displaySnackbar(t('parking-dashboard:notifications.checkoutError'), true)
    } finally {
      closeCheckoutModal()
    }
  }

  const fetchValetSettings = useCallback(async () => {
    const resp = await API.getValetSettings(buildingData.id)
    setValetSettings(resp.data)
  }, [buildingData, buildingData.id])

  const handleServiceFeeSettingsClick = () => {
    popupFactory.open(ServiceFeesModal, {
      initialPricing: valetSettings.pricing,
      onSave: (newPricings) => {
        return API.updateValetSettings({
          buildingId: buildingData.id,
          pricing: newPricings,
          paymentRequired: valetSettings.paymentRequired,
          freeForResidents: valetSettings.freeForResidents,
        }).then(() => {
          displaySnackbar(t('parking-dashboard:notifications.settingsUpdated'))
          fetchValetSettings()
        })
      },
    })
  }

  useEffect(() => {
    ;(async () => {
      if (buildingData.id) {
        fetchValetSettings()
      }
    })()
  }, [buildingData])

  return (
    <Dashboard.Container className={`ctp-parking-dashboard`}>
      <div className="analytics-header">
        <button
          className="button button-transparent"
          onClick={() => toggleAnalytics((prev) => !prev)}
        >
          <Icon icon="chartCircle" styles={{ width: '20px', height: '20px' }} />{' '}
          {analyticsOpen
            ? t('parking-dashboard:analytics.hide')
            : t('parking-dashboard:analytics.show')}
        </button>
      </div>
      <div>
        {analyticsOpen && (
          <AnalyticsWidget buildingId={buildingData.id} buildingTimezone={buildingData.timezone} />
        )}
      </div>
      <div>
        <div className="list-container">
          <TicketFilters
            toggleServiceFeeSettings={handleServiceFeeSettingsClick}
            toggleSettings={toggleSettings}
            setDateRange={setDateRange}
            dateRange={dateRange}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            phoneOrLicensePlateFilter={phoneOrLicensePlateFilter}
            setPhoneOrLicensePlateFilter={setPhoneOrLicensePlateFilter}
            hasValetSettings={!!Object.keys(valetSettings).length}
          />
          <div className="table-wrapper">
            <div className={`list-wrapper ${analyticsOpen ? 'analytics-open' : ''}`}>
              <FeaturedTable
                data={parkingData.tickets}
                columns={columns({
                  t,
                  toggleModal,
                  timezone: buildingData.timezone,
                  userRole,
                  toggleCheckoutModal,
                })}
                getRowId={(row) => row.ticketId}
              />
            </div>
            <div className="footer-wrapper">
              <PaginationFooter
                showOnPage={showOnPage}
                pageNumber={pageNumber}
                paginationItemsCount={parkingData.count}
                setSiteLoading={() => {}}
                setShowOnPage={setShowOnPage}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        </div>

        {modalData && (
          <DetailsModal
            ticket={modalData}
            onClose={() => toggleModal(false)}
            timezone={buildingData.timezone}
          />
        )}
        {settingsOpen && (
          <SettingsModal
            valetSettings={valetSettings}
            onClose={() => toggleSettings(false)}
            buildingId={buildingData.id}
            fetchValetSettings={fetchValetSettings}
            userRole={userRole}
          />
        )}
        {checkoutModalData && (
          <ConfirmPopup
            isOpen={!!checkoutModalData}
            title={t('parking-dashboard:checkoutConfirm.title')}
            cancelText={t('common:actions.cancel')}
            confirmText={t('parking-dashboard:checkoutConfirm.confirmText')}
            confirmCallback={checkoutVehicle}
            cancelCallback={closeCheckoutModal}
            body={t('parking-dashboard:checkoutConfirm.body', {
              plate: checkoutModalData.licensePlate,
            })}
          />
        )}
      </div>
    </Dashboard.Container>
  )
}

ParkingDashboard.propTypes = {
  buildingData: PropTypes.object,
  userRole: PropTypes.string,
}

export default connect((state) => {
  const userData = selectUserData(state)
  return {
    buildingData: selectActiveBuilding(state) || userData.buildingId || {},
    userRole: selectUserRole(state),
  }
})(ParkingDashboard)
